import React, { useEffect, useState } from "react";
import { Button, Drawer } from "antd";
import dayjs from "dayjs";
import Icon from "@mdi/react";
import { mdiAirplane } from "@mdi/js";
import { BrandIcon, CalendarIcon, HomeIcon, PinIcon } from "../search-icons";
import OptionPickerSheet from "./OptionPickerSheet";
import MonthPickerSheet from "./MonthPickerSheet";
import BrandPickerSheet from "./BrandPickerSheet";
import SheetHeaderClose from "./SheetHeaderClose";
import { PickerGroup, labelForValue } from "./PickerList";
import { BrandOption } from "./BrandCards";
import { monthFormat } from "../../../../utils/antd.props";
import type { ListingFilterState } from "../../../../utils/listingSearchParams";

interface Props {
  open: boolean;
  onClose: () => void;
  filters: ListingFilterState;
  destinationGroups: PickerGroup[];
  departureGroups: PickerGroup[];
  brands: BrandOption[];
  onSearch: (next: ListingFilterState) => void;
}

type SubSheet = "destination" | "departure" | "date" | "brands" | null;

export default function ModifySearchSheet({
  open,
  onClose,
  filters,
  destinationGroups,
  departureGroups,
  brands,
  onSearch,
}: Props) {
  const [draft, setDraft] = useState<ListingFilterState>(filters);
  const [sub, setSub] = useState<SubSheet>(null);

  useEffect(() => {
    if (open) setDraft(filters);
  }, [open, filters]);

  const brandLabel = (value: string) => brands.find((brand) => brand.value === value)?.label || value;
  const departureLabel = labelForValue(departureGroups, draft.exEnd[0] || "");

  const search = () => {
    onSearch(draft);
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="bottom"
      height="100%"
      title="Modify Search"
      closable={false}
      extra={<SheetHeaderClose onClose={onClose} />}
      rootClassName="v2-sheet v2-sheet--search"
      footer={
        <Button type="primary" block className="v2-sheet__submit" onClick={search}>
          Search
        </Button>
      }
    >
      <div className="v2-modify">
        <button type="button" className="v2-modify__row" onClick={() => setSub("destination")}>
          <PinIcon size={20} />
          <span className={`v2-modify__value ${draft.keyword ? "" : "is-empty"}`}>
            {draft.keyword || "Where would you like to go?"}
          </span>
        </button>

        <button type="button" className="v2-modify__row" onClick={() => setSub("departure")}>
          <HomeIcon size={20} />
          <span className={`v2-modify__value ${departureLabel ? "" : "is-empty"}`}>
            {departureLabel || "Departure From"}
          </span>
        </button>

        <button type="button" className="v2-modify__row" onClick={() => setSub("date")}>
          <CalendarIcon size={20} />
          <span className={`v2-modify__value ${draft.date ? "" : "is-empty"}`}>
            {draft.date ? dayjs(draft.date).format(monthFormat) : "Travel Date"}
          </span>
        </button>

        <button type="button" className="v2-modify__row v2-modify__row--brands" onClick={() => setSub("brands")}>
          <BrandIcon size={20} />
          {draft.brands.length > 0 ? (
            <span className="v2-modify__tags">
              {draft.brands.map((brand) => (
                <span className="v2-modify__tag" key={brand}>
                  {brandLabel(brand)}
                </span>
              ))}
            </span>
          ) : (
            <span className="v2-modify__value is-empty">Brand</span>
          )}
        </button>
      </div>

      <OptionPickerSheet
        open={sub === "destination"}
        onClose={() => setSub(null)}
        title="To Where?"
        placeholder="Where would you like to go?"
        fieldIcon={<PinIcon size={20} />}
        rowIcon={<PinIcon size={16} />}
        groups={destinationGroups}
        value={draft.keyword}
        allowFreeText
        onSelect={(option) => {
          setDraft({ ...draft, keyword: option.value, locationId: null });
          setSub(null);
        }}
      />

      <OptionPickerSheet
        open={sub === "departure"}
        onClose={() => setSub(null)}
        title="Departure From"
        placeholder="Departure From"
        fieldIcon={<HomeIcon size={20} />}
        rowIcon={<Icon path={mdiAirplane} size="16px" />}
        groups={departureGroups}
        value={draft.exEnd[0] || ""}
        onSelect={(option) => {
          setDraft({ ...draft, exEnd: [option.value] });
          setSub(null);
        }}
      />

      <MonthPickerSheet
        open={sub === "date"}
        onClose={() => setSub(null)}
        value={draft.date}
        onApply={(date) => setDraft({ ...draft, date })}
      />

      <BrandPickerSheet
        open={sub === "brands"}
        onClose={() => setSub(null)}
        brands={brands}
        value={draft.brands}
        onApply={(next) => setDraft({ ...draft, brands: next })}
      />
    </Drawer>
  );
}
